import React, { useState, useEffect, useCallback } from 'react';
import { Eye, Loader2, AlertCircle, ArrowLeft, Lock, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { publicAPI, getErrorMessage } from '../lib/api';
import SampleReportSection from '../components/SampleReportSection';
import SecurePDFViewer from '../components/SecurePDFViewer';

const OurPerformance = () => {
  const navigate = useNavigate();
  
  // State for reports
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // State for PDF viewer
  const [selectedReport, setSelectedReport] = useState(null);
  const [pdfBlob, setPdfBlob] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfError, setPdfError] = useState(null);

  // State for lead capture modal
  const [showLeadModal, setShowLeadModal] = useState(false);
  const [leadFormReport, setLeadFormReport] = useState(null);
  const [leadForm, setLeadForm] = useState({
    name: '',
    email: '',
    whatsapp: '',
  });
  const [leadSubmitting, setLeadSubmitting] = useState(false);
  const [leadError, setLeadError] = useState(null);

  // Store verified email in localStorage for session persistence
  const getVerifiedEmail = () => localStorage.getItem('performance_report_email');
  const setVerifiedEmail = (email) => localStorage.setItem('performance_report_email', email);

  // Fetch reports from API
  const fetchReports = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await publicAPI.getPerformanceReports({ page_size: 100 });
      const { data } = response.data;
      setReports(data || []);
    } catch (err) {
      console.error('Failed to fetch reports:', err);
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial fetch
  useEffect(() => {
    fetchReports();
  }, [fetchReports]);

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    }).replace(/\//g, '-');
  };

  // Format price
  const formatPrice = (price) => {
    if (!price) return '-';
    return `₹${parseFloat(price).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  // Format percentage
  const formatPercentage = (value) => {
    if (value === null || value === undefined) return '-';
    const num = parseFloat(value);
    const sign = num >= 0 ? '+' : '';
    return `${sign}${num.toFixed(2)}%`;
  };

  // Handle view report click
  const handleViewReport = async (report) => {
    // Check if report requires subscription
    if (report.require_subscription) {
      navigate('/pricing');
      return;
    }

    // Check if report doesn't allow download (no report available)
    if (!report.allow_download || !report.has_report) {
      return;
    }

    // Check if user has already submitted lead
    const email = getVerifiedEmail();
    if (email) {
      // User has already submitted lead, load PDF directly
      await loadPDF(report, email);
    } else {
      // Show lead capture form
      setLeadFormReport(report);
      setShowLeadModal(true);
    }
  };

  // Load PDF after lead verification
  const loadPDF = async (report, email) => {
    setSelectedReport(report);
    setPdfLoading(true);
    setPdfError(null);
    setPdfBlob(null);

    try {
      const response = await publicAPI.getPerformanceReportPDF(report.id, email);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      setPdfBlob(blob);
    } catch (err) {
      console.error('Failed to fetch PDF:', err);
      const errorData = err.response?.data;
      if (errorData?.code === 'LEAD_NOT_FOUND') {
        // Lead not found, show form again
        setSelectedReport(null);
        setLeadFormReport(report);
        setShowLeadModal(true);
        localStorage.removeItem('performance_report_email');
      } else {
        setPdfError(getErrorMessage(err));
      }
    } finally {
      setPdfLoading(false);
    }
  };

  // Handle lead form submit
  const handleLeadSubmit = async (e) => {
    e.preventDefault();
    setLeadSubmitting(true);
    setLeadError(null);

    try {
      await publicAPI.submitPerformanceReportLead({
        ...leadForm,
        report_id: leadFormReport.id,
      });

      // Store email for future requests
      setVerifiedEmail(leadForm.email);

      // Close modal and load PDF
      setShowLeadModal(false);
      await loadPDF(leadFormReport, leadForm.email);

      // Reset form
      setLeadForm({ name: '', email: '', whatsapp: '' });
      setLeadFormReport(null);
    } catch (err) {
      console.error('Failed to submit lead:', err);
      setLeadError(getErrorMessage(err));
    } finally {
      setLeadSubmitting(false);
    }
  };

  // Back to list
  const handleBackToList = () => {
    setSelectedReport(null);
    setPdfBlob(null);
    setPdfError(null);
  };

  // Get action button for report
  const getActionButton = (report) => {
    // If subscription required, show upgrade button
    if (report.require_subscription) {
      return (
        <button
          onClick={() => navigate('/pricing')}
          className="inline-flex items-center gap-1 px-3 py-1.5 bg-amber-500 text-white rounded-lg text-xs font-medium hover:bg-amber-600 transition-colors"
        >
          <Lock size={14} />
          Subscribe
        </button>
      );
    }

    // If download allowed and has report, show view button
    if (report.allow_download && report.has_report) {
      return (
        <button
          onClick={() => handleViewReport(report)}
          className="inline-flex items-center gap-1 px-3 py-1.5 bg-blue-600 text-white rounded-lg text-xs font-medium hover:bg-blue-700 transition-colors"
        >
          <Eye size={14} />
          View Report
        </button>
      );
    }

    // No report available
    return (
      <span className="text-gray-400 text-xs">-</span>
    );
  };

  // PDF Viewer View
  if (selectedReport) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="container mx-auto px-6 py-8">
          {/* Back Button */}
          <button
            onClick={handleBackToList}
            className="flex items-center gap-2 px-4 py-2 mb-6 bg-gray-800 text-white rounded-lg hover:bg-gray-900 transition-colors"
          >
            <ArrowLeft size={20} />
            Back to Performance
          </button>

          {/* Report Details */}
          <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              {selectedReport.recommendation_stock}
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <div>
                <p className="text-sm text-gray-500 mb-1">Recommendation Date</p>
                <p className="font-semibold text-gray-900">
                  {formatDate(selectedReport.recommendation_date)}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500 mb-1">Buy Price</p>
                <p className="font-semibold text-gray-900">
                  {formatPrice(selectedReport.recommended_price)}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500 mb-1">Exit Price</p>
                <p className="font-semibold text-gray-900">
                  {formatPrice(selectedReport.exit_price)}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500 mb-1">Gain/Loss</p>
                <p className={`font-semibold ${
                  parseFloat(selectedReport.percentage_gain_loss) >= 0 
                    ? 'text-green-600' 
                    : 'text-red-600'
                }`}>
                  {formatPercentage(selectedReport.percentage_gain_loss)}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500 mb-1">Status</p>
                <span className={`inline-block px-3 py-1 rounded-full text-sm font-semibold ${
                  selectedReport.status?.toLowerCase() === 'open'
                    ? 'bg-green-100 text-green-700'
                    : 'bg-gray-100 text-gray-700'
                }`}>
                  {selectedReport.status || '-'}
                </span>
              </div>
            </div>
          </div>

          {/* PDF Viewer */}
          {pdfLoading ? (
            <div className="bg-white rounded-lg shadow-lg p-12 flex items-center justify-center">
              <div className="text-center">
                <Loader2 className="animate-spin mx-auto mb-3 text-blue-600" size={48} />
                <p className="text-gray-600">Loading report PDF...</p>
              </div>
            </div>
          ) : pdfError ? (
            <div className="bg-white rounded-lg shadow-lg p-12 flex items-center justify-center">
              <div className="text-center text-red-500">
                <AlertCircle className="mx-auto mb-3" size={48} />
                <p className="font-semibold mb-2">Failed to load PDF</p>
                <p className="text-sm text-gray-500">{pdfError}</p>
              </div>
            </div>
          ) : pdfBlob ? (
            <SecurePDFViewer pdfBlob={pdfBlob} height="700px" />
          ) : null}
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Lead Capture Modal */}
      {showLeadModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full shadow-xl">
            <div className="p-6">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="text-xl font-bold text-gray-900">
                    View Performance Report
                  </h3>
                  <p className="text-sm text-gray-600 mt-1">
                    Please provide your details to access the report for{' '}
                    <strong>{leadFormReport?.recommendation_stock}</strong>
                  </p>
                </div>
                <button 
                  onClick={() => {
                    setShowLeadModal(false);
                    setLeadFormReport(null);
                    setLeadError(null);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X size={24} />
                </button>
              </div>

              <form onSubmit={handleLeadSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Full Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    value={leadForm.name}
                    onChange={(e) => setLeadForm({ ...leadForm, name: e.target.value })}
                    placeholder="Enter your name"
                    className="w-full border border-gray-300 rounded-lg px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email Address <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    required
                    value={leadForm.email}
                    onChange={(e) => setLeadForm({ ...leadForm, email: e.target.value })}
                    placeholder="Enter your email"
                    className="w-full border border-gray-300 rounded-lg px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    WhatsApp Number <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="tel"
                    required
                    value={leadForm.whatsapp}
                    onChange={(e) => setLeadForm({ ...leadForm, whatsapp: e.target.value })}
                    placeholder="+91XXXXXXXXXX"
                    className="w-full border border-gray-300 rounded-lg px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                {leadError && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm">
                    {leadError}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={leadSubmitting}
                  className="w-full bg-amber-500 text-white py-3 rounded-lg font-semibold hover:bg-amber-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {leadSubmitting ? (
                    <>
                      <Loader2 className="animate-spin" size={20} />
                      Submitting...
                    </>
                  ) : (
                    'View Report'
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Hero Section */}
      <section className="bg-white py-16">
        <div className="container mx-auto px-6">
          <h1 className="text-4xl font-bold mb-8 text-gray-900">
            About Climb Capital and Our Research Journey
          </h1>
          <div className="space-y-6 text-gray-700 leading-relaxed max-w-5xl">
            <p>
              Climb Capital was incorporated on 10th April, 2024, with a vision to deliver high-quality, research-driven 
              investment insights to retail and institutional investors. Shortly after our incorporation, we obtained 
              registration as a SEBI-registered Research Analyst firm, marking a significant milestone in our commitment 
              to maintaining the highest standards of transparency, compliance, and investor protection.
            </p>
            <p>
              Since our inception, our core philosophy has been centered around fundamental research, long-term value 
              creation, and unbiased stock recommendations. We aim to identify companies with strong business models, 
              competent management, sustainable competitive advantages, and attractive valuations.
            </p>
            <p>
              As a young and dynamic research firm, we take pride in presenting the performance of our stock recommendations 
              since inception. These results highlight our disciplined methodology, robust research framework, and steadfast 
              commitment to creating wealth for our investors. Below is a summary of how our stock picks have performed 
              since our SEBI registration.
            </p>
          </div>
        </div>
      </section>

      {/* Performance Table */}
      <section className="py-12 bg-gray-50">
        <div className="container mx-auto px-6">
          <h2 className="text-2xl font-bold mb-6 text-gray-900">OUR STOCK PERFORMANCE</h2>
          
          {loading ? (
            <div className="bg-white rounded-lg shadow-lg p-12 flex items-center justify-center">
              <div className="text-center">
                <Loader2 className="animate-spin mx-auto mb-3 text-blue-600" size={48} />
                <p className="text-gray-600">Loading performance data...</p>
              </div>
            </div>
          ) : error ? (
            <div className="bg-white rounded-lg shadow-lg p-12 flex items-center justify-center">
              <div className="text-center text-red-500">
                <AlertCircle className="mx-auto mb-3" size={48} />
                <p className="font-semibold mb-2">Failed to load data</p>
                <p className="text-sm text-gray-500">{error}</p>
                <button
                  onClick={fetchReports}
                  className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Retry
                </button>
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow-lg overflow-x-auto">
              <table className="w-full">
                <thead className="bg-[#1b2d4a] text-white">
                  <tr>
                    <th className="px-6 py-4 text-left text-sm font-semibold">Recommendation Date</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold">Recommendation Stock</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold">Recommended Price</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold">Exit Price</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold">Percentage Gain/loss</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold">Status</th>
                    <th className="px-6 py-4 text-center text-sm font-semibold">Report</th>
                  </tr>
                </thead>
                <tbody>
                  {reports.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="px-6 py-12 text-center text-gray-500">
                        No performance data available yet.
                      </td>
                    </tr>
                  ) : (
                    reports.map((report) => (
                      <tr key={report.id} className="border-b hover:bg-gray-50">
                        <td className="px-6 py-4 text-sm text-gray-700">
                          {formatDate(report.recommendation_date)}
                        </td>
                        <td className="px-6 py-4 text-sm text-blue-600 font-medium">
                          {report.recommendation_stock}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-700">
                          {formatPrice(report.recommended_price)}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-700">
                          {formatPrice(report.exit_price)}
                        </td>
                        <td className={`px-6 py-4 text-sm font-medium ${
                          parseFloat(report.percentage_gain_loss) >= 0 
                            ? 'text-green-600' 
                            : 'text-red-600'
                        }`}>
                          {formatPercentage(report.percentage_gain_loss)}
                        </td>
                        <td className="px-6 py-4 text-sm">
                          <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                            report.status?.toLowerCase() === 'open' 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-gray-100 text-gray-800'
                          }`}>
                            {report.status || '-'}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-center">
                          {getActionButton(report)}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}

          <div className="mt-8 bg-blue-50 border-l-4 border-blue-500 p-6 rounded">
            <p className="text-sm text-gray-700 leading-relaxed">
              <strong>Note:</strong> The performance data shown above represents actual recommendations made to our clients. 
              All data is maintained as per SEBI guidelines and is available for verification. Past performance does not guarantee future results.
            </p>
          </div>
        </div>
      </section>

      {/* Sample Report Section */}
      <SampleReportSection />
    </div>
  );
};

export default OurPerformance;
